<script>
    import Select from "svelte-select/Select.svelte";

    import { NotificationModal } from "../../../Components/Common/index.svelte";

    import { loggedInDoctorDetail, user } from "../../../stores";

    import {
        loadDoctors,
        splitCamelCase,
    } from "../../../Utils/utilFunctions.svelte";

    import { questionnairePropertyMap } from "../../../Utils/utilConstants.svelte";

    import { searchGroupsFromCache } from "../../../Utils/DataLoaders.svelte";

    export let isModalOpen = false;
    export let type = "";
    export let settingObj = {};
    export let closeModal = () => {};
    export let confirmAction = () => {};

    $: modalTitle = _getModalTitle(type);

    $: isUserDoctor = $user.role == 2;

    $: showSelectGroup =
        type == "group" && ($user.role == 100 || $user.role == 20);

    $: showSelectDoctor =
        type == "doctor" &&
        ($user.role == 100 || $user.role == 20 || $user.role == 80);

    $: isSaveBtnDisabled = _checkSaveBtnDisabled(
        type,
        selectedGroup,
        selectedDoctor,
        isUserDoctor,
        showSelectGroup,
        showSelectDoctor,
    );

    let isSendingObj = false;

    let selectedGroup = {},
        selectedDoctor = {};

    const handleConfirm = async () => {
        isSendingObj = true;

        let result = {
            name: settingObj.name,
            groupId: isUserDoctor
                ? ($user.groupId ?? 0)
                : type == "group"
                  ? showSelectGroup
                      ? selectedGroup.group.id
                      : ($user.groupId ?? 0)
                  : type == "doctor"
                    ? selectedDoctor.doctor.groupId
                    : settingObj.groupId,
            accountId: isUserDoctor
                ? $loggedInDoctorDetail.id
                : type == "doctor"
                  ? selectedDoctor.doctor.id
                  : settingObj.accountId,
            url: _getUrlBySettingObj(settingObj),
        };

        delete settingObj.name;
        delete settingObj.groupId;
        delete settingObj.accountId;
        delete settingObj.url;

        result.settings = JSON.stringify(settingObj);

        await confirmAction(result, type);

        isSendingObj = false;

        selectedGroup = {};
        selectedDoctor = {};
    };

    const handleToggleProperty = (key) => {
        settingObj = {
            ...settingObj,
            [key]: Math.abs(Number(settingObj[key]) - 1),
        };

        if (
            key == "showPatientFollowup" &&
            settingObj.hasOwnProperty("showPatientCallback")
        )
            settingObj.showPatientCallback = settingObj.showPatientFollowup;
    };

    const clearSelectedGroup = () => (selectedGroup = {});

    const clearSelectedDoctor = () => (selectedDoctor = {});

    // HELPER FUNCTIONS
    const _getModalTitle = (type) => {
        switch (type) {
            case "group":
                return "Customise Group Questionnaire";
            case "doctor":
                return "Customise Doctor Questionnaire";
            case "edit":
                return "Edit Questionnaire";
            default:
                return "Customise Questionnaire";
        }
    };

    const _checkSaveBtnDisabled = (
        type,
        selectedGroup,
        selectedDoctor,
        isUserDoctor,
        showSelectGroup,
        showSelectDoctor,
    ) => {
        if (isUserDoctor) return false;

        if (type == "group" && showSelectGroup)
            return !selectedGroup || !selectedGroup.group;
        if (type == "doctor" && showSelectDoctor)
            return !selectedDoctor || !selectedDoctor.doctor;

        return false;
    };

    const _getUrlBySettingObj = (obj) => {
        let res = "";
        for (const key of Object.keys(obj)) {
            if (key.startsWith("show") && !obj[key])
                res += `${res.length ? "&" : "?"}${key}=0`;
        }
        return `${obj.url.split("?")[0]}${res}`;
    };
</script>

<NotificationModal
    {isModalOpen}
    title={modalTitle}
    cancelText="Close"
    closeModalFunction={closeModal}
    {closeModal}
    showConfirmBtn={false}
    isConfirming={isSendingObj}
    noContentPadding
    btnOptions={[
        {
            text:
                type == "edit"
                    ? isSendingObj
                        ? "Updating"
                        : "Update"
                    : isSendingObj
                      ? "Saving"
                      : "Customise",
            loading: isSendingObj || isSaveBtnDisabled,
            function: () => handleConfirm(),
        },
    ]}
>
    <div class="flex flex-col gap-y-4 overflow-y-scroll p-6">
        {#if showSelectGroup}
            <div class="flex items-center gap-x-4">
                <p class="title">Select group:</p>

                <Select
                    bind:value={selectedGroup.group}
                    class="flex flex-1 max-h-[35px] rounded-full"
                    loadOptions={searchGroupsFromCache}
                    itemId="id"
                    label="name"
                    on:clear={clearSelectedGroup}
                />
            </div>
        {/if}

        {#if showSelectDoctor}
            <div class="flex items-center gap-x-4">
                <p class="title">Select doctor:</p>

                <Select
                    bind:value={selectedDoctor.doctor}
                    class="flex flex-1 max-h-[35px] rounded-full"
                    loadOptions={loadDoctors}
                    itemId="id"
                    label="displayName"
                    on:clear={clearSelectedDoctor}
                />
            </div>
        {/if}

        <div class="flex gap-x-4">
            <p class="title">Name:</p>

            <input
                type="text"
                class="input input-sm input-bordered flex-1"
                autocomplete="off"
                bind:value={settingObj.name}
            />
        </div>

        {#each Object.entries(settingObj).filter(([key]) => {
            if ("showPatientFollowup" in settingObj && "showPatientCallback" in settingObj) {
                return key !== "showPatientCallback";
            }
            return true;
        }) as [key, value]}
            {#if key.startsWith("show")}
                <div class="flex flex-1 justify-between gap-x-4">
                    <p class="title">
                        {(questionnairePropertyMap[key] ??
                        key.startsWith("show"))
                            ? splitCamelCase(key.slice(4))
                            : key}
                    </p>

                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        id={key}
                        bind:checked={value}
                        on:change|preventDefault={() =>
                            handleToggleProperty(key)}
                    />
                </div>
            {/if}
        {/each}
    </div>
</NotificationModal>

<style>
    .title {
        width: auto;
        min-width: 120px;
    }
</style>
